import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { useAuth } from "../context/auth";

type DeliverySummary = {
	id: string;
	endpointId: string;
	eventType: string;
	statusCode: number | null;
	success: boolean | null;
	attempts: number;
	lastAttemptAt: string | null;
	nextRetryAt: string | null;
	createdAt: string;
};

type DeliveryDetail = DeliverySummary & {
	payload: string;
	responseBody: string | null;
};

function formatTimestamp(dateStr: string | null): string {
	if (!dateStr) return "—";
	return new Date(dateStr).toLocaleString();
}

function statusDisplay(statusCode: number | null): string {
	if (statusCode === null) return "Pending";
	if (statusCode === 0) return "Network Error";
	return String(statusCode);
}

export function DeliveryLogPage() {
	const { workspaceSlug, endpointId } = useParams<{
		workspaceSlug: string;
		endpointId: string;
	}>();
	const { hasPermission } = useAuth();

	const [deliveries, setDeliveries] = useState<DeliverySummary[]>([]);
	const [nextCursor, setNextCursor] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const [expandedId, setExpandedId] = useState<string | null>(null);
	const [detail, setDetail] = useState<DeliveryDetail | null>(null);
	const [detailLoading, setDetailLoading] = useState(false);

	const fetchDeliveries = useCallback(
		async (cursor?: string, signal?: AbortSignal) => {
			try {
				setError(null);
				const params = new URLSearchParams();
				if (cursor) params.set("cursor", cursor);
				params.set("limit", "20");
				const res = await fetch(
					`/api/tenant/${workspaceSlug}/webhooks/${endpointId}/deliveries?${params}`,
					{ credentials: "include", signal },
				);
				if (!res.ok) {
					throw new Error(`Failed to load deliveries (${res.status})`);
				}
				const data = (await res.json()) as {
					deliveries: DeliverySummary[];
					nextCursor: string | null;
				};
				if (cursor) {
					setDeliveries((prev) => [...prev, ...data.deliveries]);
				} else {
					setDeliveries(data.deliveries);
				}
				setNextCursor(data.nextCursor);
			} catch (err) {
				if (err instanceof DOMException && err.name === "AbortError") return;
				setError(
					err instanceof Error ? err.message : "Failed to load deliveries",
				);
			} finally {
				setLoading(false);
			}
		},
		[workspaceSlug, endpointId],
	);

	useEffect(() => {
		const controller = new AbortController();
		fetchDeliveries(undefined, controller.signal);
		return () => controller.abort();
	}, [fetchDeliveries]);

	async function handleExpand(deliveryId: string) {
		if (expandedId === deliveryId) {
			setExpandedId(null);
			setDetail(null);
			return;
		}
		setExpandedId(deliveryId);
		setDetail(null);
		setDetailLoading(true);
		try {
			const res = await fetch(
				`/api/tenant/${workspaceSlug}/webhooks/${endpointId}/deliveries/${deliveryId}`,
				{ credentials: "include" },
			);
			if (!res.ok) {
				throw new Error(`Failed to load delivery detail (${res.status})`);
			}
			const data = (await res.json()) as DeliveryDetail;
			setDetail(data);
		} catch (err) {
			setError(
				err instanceof Error ? err.message : "Failed to load delivery detail",
			);
			setExpandedId(null);
		} finally {
			setDetailLoading(false);
		}
	}

	function formatJson(raw: string | null): string {
		if (!raw) return "(empty)";
		try {
			return JSON.stringify(JSON.parse(raw), null, 2);
		} catch {
			return raw;
		}
	}

	if (!hasPermission("webhooks", "read")) {
		return (
			<div className="max-w-2xl mx-auto mt-8">
				<p className="text-sm text-gray-600">
					You don't have access to this page.
				</p>
			</div>
		);
	}

	return (
		<div className="max-w-3xl mx-auto mt-8" data-testid="delivery-log-page">
			<div className="flex items-center justify-between mb-6">
				<h1 className="text-2xl font-bold text-gray-900">Delivery Log</h1>
				<Link
					to={`/${workspaceSlug}/settings/webhooks`}
					className="text-sm text-blue-600 hover:text-blue-800"
				>
					Back to Webhooks
				</Link>
			</div>

			{error && (
				<div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3 mb-4">
					{error}
				</div>
			)}

			<section className="bg-white border border-gray-200 rounded-lg p-6">
				{loading ? (
					<div className="text-sm text-gray-500">Loading deliveries...</div>
				) : deliveries.length === 0 ? (
					<p className="text-sm text-gray-400 text-center py-6">
						No deliveries recorded yet.
					</p>
				) : (
					<>
						<table className="w-full text-sm text-left">
							<thead>
								<tr className="border-b border-gray-200 text-gray-500 text-xs uppercase tracking-wider">
									<th className="py-2 pr-4 font-medium">Event</th>
									<th className="py-2 pr-4 font-medium">Status</th>
									<th className="py-2 pr-4 font-medium">Attempts</th>
									<th className="py-2 pr-4 font-medium">Last Attempt</th>
									<th className="py-2 pr-4 font-medium">Next Retry</th>
									<th className="py-2 font-medium" />
								</tr>
							</thead>
							<tbody>
								{deliveries.map((d) => (
									<>
										<tr
											key={d.id}
											className="border-b border-gray-100"
											data-testid="delivery-row"
										>
											<td className="py-3 pr-4 text-gray-900 font-mono text-xs">
												{d.eventType}
											</td>
											<td className="py-3 pr-4">
												<span
													className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
														d.success === true
															? "bg-green-100 text-green-800"
															: d.success === false
																? "bg-red-100 text-red-800"
																: "bg-gray-100 text-gray-600"
													}`}
													data-testid="delivery-status-badge"
												>
													{statusDisplay(d.statusCode)}
												</span>
											</td>
											<td className="py-3 pr-4 text-gray-600 text-xs">
												{d.attempts}
											</td>
											<td className="py-3 pr-4 text-gray-500 text-xs">
												{formatTimestamp(d.lastAttemptAt)}
											</td>
											<td className="py-3 pr-4 text-gray-500 text-xs">
												{formatTimestamp(d.nextRetryAt)}
											</td>
											<td className="py-3">
												<button
													type="button"
													onClick={() => handleExpand(d.id)}
													className="text-blue-600 hover:text-blue-800 text-xs cursor-pointer"
													data-testid="delivery-expand-btn"
												>
													{expandedId === d.id ? "Collapse" : "Expand"}
												</button>
											</td>
										</tr>
										{expandedId === d.id && (
											<tr key={`${d.id}-detail`}>
												<td colSpan={6} className="px-4 py-3 bg-gray-50">
													{detailLoading ? (
														<div className="text-sm text-gray-500">
															Loading detail...
														</div>
													) : detail ? (
														<div className="space-y-3">
															<div>
																<h4 className="text-xs font-semibold text-gray-700 mb-1">
																	Request Payload
																</h4>
																<pre
																	className="text-xs bg-white border border-gray-200 rounded p-2 overflow-x-auto max-h-60"
																	data-testid="delivery-payload"
																>
																	{formatJson(detail.payload)}
																</pre>
															</div>
															<div>
																<h4 className="text-xs font-semibold text-gray-700 mb-1">
																	Response Body
																</h4>
																<pre
																	className="text-xs bg-white border border-gray-200 rounded p-2 overflow-x-auto max-h-60"
																	data-testid="delivery-response-body"
																>
																	{formatJson(detail.responseBody)}
																</pre>
															</div>
														</div>
													) : null}
												</td>
											</tr>
										)}
									</>
								))}
							</tbody>
						</table>
						{nextCursor && (
							<div className="mt-4 text-center">
								<button
									type="button"
									onClick={() => fetchDeliveries(nextCursor)}
									className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer"
									data-testid="load-more-btn"
								>
									Load More
								</button>
							</div>
						)}
					</>
				)}
			</section>
		</div>
	);
}
